/**
 * protectedOrgs.js
 * Organizaciones protegidas a nivel de plataforma.
 *
 * QUÉ SIGNIFICA "PROTEGIDA":
 *   1. Exenta del muro de trial vencido (TenantContext.isTrialExpired).
 *   2. No borrable por su propio dueño (lo impone la BD, en
 *      delete_organization_self_service — ver migración 20260804).
 *
 * FUENTE DE VERDAD: la tabla `platform_protected_orgs`, que solo escribe un
 * superadmin. Esta constante es el RESPALDO, no la fuente.
 *
 * ⚠️ POR QUÉ EXISTE EL RESPALDO — la razón es todo el punto de este archivo:
 * la exención existe para que el revisor de App Store no se encuentre un muro
 * de pago. Si la exención dependiera SOLO de una lectura a la base, entonces
 * una red caída, una policy mal puesta o una fila que alguien borró producirían
 * exactamente el fallo que esto previene: muro → rechazo 2.1 → semanas perdidas.
 *
 * Por eso el cálculo es la UNIÓN de las dos fuentes, nunca la intersección:
 *   exenta = está en la constante  O  está en la tabla
 *
 * La tabla puede AÑADIR organizaciones sin desplegar; no puede QUITAR las de
 * aquí. Para retirar una de esta lista hace falta un deploy, que es
 * deliberadamente el camino lento: des-proteger la demo por accidente cuesta
 * un rechazo de Apple.
 */

/**
 * Respaldo mínimo. Solo organizaciones cuya caída rompe una revisión de Apple.
 * NO es la lista general de orgs protegidas — esa vive en la tabla.
 */
export const FALLBACK_PROTECTED_ORG_IDS = Object.freeze([
    // TheSmartCarWash — la organización demo que se le entrega a App Store Review.
    // Sus credenciales viajan en las App Review Notes.
    'f2d6129d-f892-46ee-9cf6-e472bfe36117',
    // TheSmartWash — segunda organización de la cuenta QA multi-sucursal.
    // Va aquí porque el switcher del propio overlay de trial vencido lleva a
    // ella: eximir solo la primera dejaría el muro a un toque de distancia.
    '1e1f5ee3-1692-4815-8a65-c24909ffde98',
]);

/**
 * ¿Está esta organización exenta del muro de trial?
 *
 * @param {string|null|undefined} organizationId
 * @param {string[]} fromDb - ids leídos de platform_protected_orgs (puede venir
 *                            vacío si la lectura falló; es intencionadamente
 *                            inofensivo, ver nota de arriba)
 * @returns {boolean}
 */
export const isProtectedOrg = (organizationId, fromDb = []) => {
    if (!organizationId) return false;
    const id = String(organizationId);
    return FALLBACK_PROTECTED_ORG_IDS.includes(id)
        || (fromDb || []).map(String).includes(id);
};
